import { useCallback, useRef } from 'react';
import confetti, { Options } from 'canvas-confetti';


// palette with some complementary colors based on our primary brand color
const BRANDED_CONFETTI_COLORS: string[] = [
    '#107F88',
    '#1AABB7',
    '#5DCDD6',
    '#0D5C7A',
];

// I wanted to make sure typescript strictly enforced preset names and would suggest them.
// while also still suggesting confetti config Options
function buildOptionType(options: Options): Options {
    return options;
}

export const CONFETTI_PRESETS = {
    burst: buildOptionType({
        particleCount: 120,
        spread: 70,
        origin: { y: 0.6 },
        colors: BRANDED_CONFETTI_COLORS,
    }),
    snow: buildOptionType({
        particleCount: 200,
        angle: 90,
        spread: 120,
        origin: { y: .1 },
        gravity: .85,
        drift: .3,
        ticks: 1000,
        colors: BRANDED_CONFETTI_COLORS,
    }),
};

export interface CelebrateOptions extends Options {
    preset?: keyof typeof CONFETTI_PRESETS;
}

export interface UseVZConfettiOptions {
    cooldownMs?: number;
}

export interface UseVZConfettiReturn {
    celebrate: (options?: CelebrateOptions) => void;
    reset: () => void;
}

export function useVZConfetti(options: UseVZConfettiOptions = {}): UseVZConfettiReturn {
    const {cooldownMs = 1000} = options;
    const lastFiredAt = useRef<number>(0);

    const isCoolingDown = useCallback(() => {
        if (cooldownMs === 0) return false;
        return Date.now() - lastFiredAt.current < cooldownMs;
    }, [cooldownMs]);

    const celebrate = useCallback((options: CelebrateOptions = {}): void => {
        if (isCoolingDown()) return;

        lastFiredAt.current = Date.now();

        const { preset, ...overrides } = options;
        const baseOptions = preset ? CONFETTI_PRESETS[preset] : CONFETTI_PRESETS["burst"];

        confetti({
            ...baseOptions,
            ...overrides,
            disableForReducedMotion: true, // accessibility, always respect device preference for reduced motion
        });
    }, [isCoolingDown]);

    const reset = useCallback((): void => {
        confetti.reset();
    }, []);

    return { celebrate, reset };
}
